import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  KÖZÖSSÉGI SZOLGÁLAT (IKSZ) – tanulónkénti óraszám és tételek.
 *
 *  Az érettségihez 50 óra kell. A tételek a KRÉTÁ-ból jönnek (admin szinkron), és itt is
 *  rögzíthetők; az itt rögzített, módosított vagy törölt tétel „KRÉTÁ-ba visszaírandó"
 *  jelzést kap, amíg a KRÉTÁ-ba vissza nem kerül.
 *
 *  Az osztályfőnök a saját osztályait látja, a közösségi szolgálat adminisztrátora mindenkit –
 *  ezt a szerver dönti el, a képernyő csak azt mutatja, amit kap.
 */

interface IkszTetel {
  id: string;
  kezdete: string;
  vege: string;
  oraszam: number;
  tipus: string;
  teljesitesi_hely: string | null;
  megjegyzes: string | null;
  forras: 'kreta' | 'sulinform';
  kreta_id: number | null;
  kreta_allapot: 'szinkronban' | 'visszairasra_var' | 'hiba';
  kreta_osztaly: string | null;
  rogzitette: string | null;
}

interface IkszTanulo {
  oktazon: string;
  nev: string;
  osztaly_id: string;
  osztaly_nev: string;
  evfolyam: number | null;
  ora: number;
  teljesitve: boolean;
  visszairasra_var: number;
  tetelek: IkszTetel[];
}

interface IkszUrlap {
  oktazon: string;
  id?: string;
  kreta: boolean;
  kezdete: string;
  vege: string;
  oraszam: number | string;
  tipus: string;
  teljesitesi_hely: string;
  megjegyzes: string;
}

type IkszAllapot = 'mind' | 'hianyzik' | 'teljesitve' | 'visszairas';

@Component({
  selector: 'app-iksz',
  templateUrl: './iksz.component.html',
  styleUrls: ['./iksz.component.scss'],
})
export class IkszComponent implements OnInit {
  intezmeny = '';
  /** Token nélküli környezetben (dev, standalone) ebből azonosít a szerver. */
  allomany = '';

  toltodik = false;
  hiba = '';
  cel = 50;
  admin = false;
  utolsoSzinkron: { indult: string; kreta_db: number } | null = null;

  tanulok: IkszTanulo[] = [];
  tipusok: { kreta_id: number; nev: string }[] = [];

  osztaly = '';
  allapot: IkszAllapot = 'mind';
  szuro = '';

  nyitott = new Set<string>();
  urlap: IkszUrlap | null = null;
  mentesFut = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(sajat?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    const um = await this.data.getFromStorage('userMetadata');
    this.allomany = String(um?.adid || um?.username || um?.preferred_username || '').trim();
    await Promise.all([this.betolt(), this.tipusokBetolt()]);
  }

  private get para(): string {
    return new URLSearchParams({ allomany: this.allomany }).toString();
  }

  async betolt() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.toltodik = true;
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/iksz/lista/${this.intezmeny}?${this.para}`));
      this.tanulok = r?.tanulok || [];
      this.cel = Number(r?.cel) || 50;
      this.admin = !!r?.admin;
      this.utolsoSzinkron = r?.utolso_szinkron || null;
      // Egy osztály esetén (osztályfőnök) nincs mit választani.
      if (this.osztalyok.length === 1) this.osztaly = this.osztalyok[0];
      else if (this.osztaly && !this.osztalyok.includes(this.osztaly)) this.osztaly = '';
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A közösségi szolgálat adatai nem tölthetők be.';
      this.tanulok = [];
    } finally {
      this.toltodik = false;
    }
  }

  async tipusokBetolt() {
    try {
      this.tipusok = (await firstValueFrom(this.http.get<any[]>('/api/iksz/tipusok'))) || [];
    } catch { this.tipusok = []; }
  }

  get osztalyok(): string[] {
    return [...new Set(this.tanulok.map((t) => t.osztaly_nev).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'hu', { numeric: true }));
  }

  get szurt(): IkszTanulo[] {
    const sz = this.szuro.trim().toLowerCase();
    return this.tanulok.filter((t) =>
      (!this.osztaly || t.osztaly_nev === this.osztaly)
      && (this.allapot === 'mind'
        || (this.allapot === 'hianyzik' && !t.teljesitve)
        || (this.allapot === 'teljesitve' && t.teljesitve)
        || (this.allapot === 'visszairas' && t.visszairasra_var > 0))
      && (!sz || t.nev.toLowerCase().includes(sz) || t.oktazon.includes(sz)));
  }

  /** Az aktuális szűrés összesítője (az osztály és a keresés szerint, állapottól függetlenül). */
  get osszesito() {
    const kor = this.tanulok.filter((t) => !this.osztaly || t.osztaly_nev === this.osztaly);
    const hianyzik = kor.filter((t) => !t.teljesitve);
    return {
      tanulo: kor.length,
      teljesitve: kor.length - hianyzik.length,
      hianyzik: hianyzik.length,
      hianyzoOra: hianyzik.reduce((s, t) => s + (this.cel - t.ora), 0),
      visszairas: kor.reduce((s, t) => s + t.visszairasra_var, 0),
    };
  }

  szazalek(t: IkszTanulo): number {
    return Math.min(100, Math.round((t.ora / this.cel) * 100));
  }

  hianyzoOra(t: IkszTanulo): number {
    return Math.max(0, this.cel - t.ora);
  }

  ora(n: number): string {
    return Number.isInteger(n) ? String(n) : n.toLocaleString('hu-HU', { maximumFractionDigits: 1 });
  }

  idoszak(x: IkszTetel): string {
    const f = (d: string) => d.replace(/-/g, '. ') + '.';
    return !x.vege || x.vege === x.kezdete ? f(x.kezdete) : `${f(x.kezdete)} – ${f(x.vege)}`;
  }

  valt(oktazon: string) {
    if (this.nyitott.has(oktazon)) {
      this.nyitott.delete(oktazon);
      if (this.urlap?.oktazon === oktazon) this.urlap = null;
    } else {
      this.nyitott.add(oktazon);
    }
  }

  ujTetel(t: IkszTanulo) {
    this.nyitott.add(t.oktazon);
    const ma = new Date().toISOString().slice(0, 10);
    this.urlap = { oktazon: t.oktazon, kreta: false, kezdete: ma, vege: ma, oraszam: '', tipus: '', teljesitesi_hely: '', megjegyzes: '' };
  }

  szerkeszt(t: IkszTanulo, x: IkszTetel) {
    this.nyitott.add(t.oktazon);
    this.urlap = {
      oktazon: t.oktazon, id: x.id, kreta: !!x.kreta_id,
      kezdete: x.kezdete, vege: x.vege || x.kezdete, oraszam: x.oraszam, tipus: x.tipus,
      teljesitesi_hely: x.teljesitesi_hely || '', megjegyzes: x.megjegyzes || '',
    };
  }

  /** A típuslista kiegészítve a tétel régi, már nem létező típusával – különben üresnek látszana. */
  tipusValasztek(): string[] {
    const nevek = this.tipusok.map((t) => t.nev);
    if (this.urlap?.tipus && !nevek.includes(this.urlap.tipus)) nevek.unshift(this.urlap.tipus);
    return nevek;
  }

  async mentes() {
    const u = this.urlap;
    if (!u) return;
    this.mentesFut = true;
    const adat = {
      allomany: this.allomany, oktazon: u.oktazon, kezdete: u.kezdete, vege: u.vege || u.kezdete,
      oraszam: u.oraszam, tipus: u.tipus, teljesitesi_hely: u.teljesitesi_hely, megjegyzes: u.megjegyzes,
    };
    try {
      if (u.id) await firstValueFrom(this.http.put(`/api/iksz/tetel/${u.id}?${this.para}`, adat));
      else await firstValueFrom(this.http.post(`/api/iksz/tetel/${this.intezmeny}?${this.para}`, adat));
      this.data.sendToast(u.id ? 'A tétel módosult.' : 'A tétel rögzítve.', 'success');
      this.urlap = null;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.mentesFut = false;
    }
  }

  async torol(x: IkszTetel) {
    const szoveg = x.kreta_id
      ? 'Törlöd a tételt? A KRÉTÁ-ban is szerepel: a törlés „KRÉTÁ-ba visszaírandó" jelzést kap.'
      : 'Törlöd a tételt?';
    if (!confirm(szoveg)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/iksz/tetel/${x.id}?${this.para}`));
      this.data.sendToast('A tétel törölve.', 'success');
      if (this.urlap?.id === x.id) this.urlap = null;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }

  trackTanulo(_: number, t: IkszTanulo) { return t.oktazon; }
  trackTetel(_: number, x: IkszTetel) { return x.id; }
}
